import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchOrderUpdates } from '@/api/orderService';
import { orderUpdatesKey, useFetchOrderUpdates } from '@/hooksApi/useOrder';
import useCustomerIO from '@/hooks/useCustomerIO';
import useOrderUpdatesEnabled from '@/hooks/useOrderUpdatesEnabled';
import type { OrderUpdates } from '@/types/orderUpdates';

export type UpdatesSection = 'calls' | 'comments' | 'whatsapp';
export type SectionState = 'loading' | 'unavailable' | 'empty' | 'ready';

// The source whose failure makes a section unreliable (PRD §5.5: "calls: null
// or sources.voip ≠ ok"). PBX / status-history only add rows, so their failure
// leaves the section showing what did load.
const SECTION_SOURCE = { calls: 'voip', comments: 'remarks', whatsapp: 'voip' } as const;
// BOXY-587 — the merchant reads comments first, then calls, then WhatsApp.
export const UPDATES_SECTIONS: UpdatesSection[] = ['comments', 'calls', 'whatsapp'];

/**
 * Only an explicit failure hides a section. `no_history` (VoIP has no record of
 * the order), a missing entry, or any future value render the list as sent —
 * usually the empty state — rather than an error.
 */
export const isSourceDown = (state: string | undefined) =>
  state === 'unavailable' || state === 'timeout';

/** A list we can render, or null when the backend sent anything else. */
export const sectionList = <K extends UpdatesSection>(data: OrderUpdates | undefined, key: K) => {
  const list = data?.[key];
  return Array.isArray(list) ? (list as NonNullable<OrderUpdates[K]>) : null;
};

export const sectionState = (
  data: OrderUpdates | undefined,
  key: UpdatesSection,
  loading: boolean,
): SectionState => {
  if (!data) return loading ? 'loading' : 'unavailable';
  const list = sectionList(data, key);
  const source = data.sources?.[SECTION_SOURCE[key]];
  if (!list || isSourceDown(source)) return 'unavailable';
  return list.length ? 'ready' : 'empty';
};

/** Container logic for the order Updates tab: lazy query, cache-bypassing refresh, analytics. */
const useOrderUpdates = (orderUid: string) => {
  const enabled = useOrderUpdatesEnabled();
  const queryClient = useQueryClient();
  const { trackEvent } = useCustomerIO();
  const query = useFetchOrderUpdates(orderUid, enabled);
  const data = query.data?.object;

  // Re-request with ?refresh=1 (Core bypasses its 60 s cache) and land the
  // answer in the same cache entry the tab reads.
  const refresh = () =>
    queryClient
      .fetchQuery(orderUpdatesKey(orderUid), () => fetchOrderUpdates(orderUid, true))
      .catch(() => undefined); // the query's own error state renders the failure

  const opened = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (!opened.current) {
      opened.current = true;
      trackEvent('updates_tab_opened', {
        order_uid: orderUid,
        counts: Object.fromEntries(
          UPDATES_SECTIONS.map((key) => [key, sectionList(data, key)?.length ?? null]),
        ),
      });
    }
    UPDATES_SECTIONS.forEach((key) => {
      if (sectionState(data, key, false) === 'unavailable') {
        const source = SECTION_SOURCE[key];
        trackEvent('updates_section_error', {
          order_uid: orderUid,
          section: key,
          source,
          state: sectionList(data, key) ? data.sources?.[source] : 'missing',
        });
      }
    });
  }, [data, orderUid, trackEvent]);

  return {
    data,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    isRefreshing: query.isFetching,
    /** When the cached answer arrived (ms epoch); 0 before the first one. */
    updatedAt: query.dataUpdatedAt,
    refresh,
    stateOf: (key: UpdatesSection) => sectionState(data, key, enabled && query.isLoading),
  };
};

export default useOrderUpdates;
